import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, CheckCircle2, Clock } from 'lucide-react';
import { PACKAGE_TIERS, PACKAGE_PAYMENT_SPLITS } from '@hmtdao2/shared';
import { api } from '../api/client';
import { TopBar } from '../components/layout/TopBar';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Chest } from '../components/ui/Chest';
import { chestForPackageItems } from '../lib/chest';
import { cn } from '../lib/cn';
import { fmt } from '../lib/format';

interface Pkg {
  id: string;
  tierItems: number;
  dailyRateBps: number;
  profitMultiplierX100: number;
  status: 'PURCHASED' | 'ACTIVE' | 'COMPLETED';
  purchasedAt: string;
  activatedAt: string | null;
  paidOut: string;
}

interface Wallet { usdtBalance: string; cpBalance: string; rpBalance: string }

export function PackagePage() {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [owned, setOwned] = useState<Pkg[]>([]);
  const [qty, setQty] = useState<Record<number, number>>(() =>
    Object.fromEntries(PACKAGE_TIERS.map((t) => [t.items, 1])),
  );
  const [buyTier, setBuyTier] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function refresh() {
    const [w, p] = await Promise.all([
      api<Wallet>('/wallet'),
      api<{ packages: Pkg[] }>('/wallet/packages'),
    ]);
    setWallet(w); setOwned(p.packages);
  }

  useEffect(() => { refresh().catch(() => { /* */ }); }, []);

  const cp = wallet?.cpBalance ?? '0';
  const rp = wallet?.rpBalance ?? '0';

  return (
    <>
      <TopBar title="Packages" />
      <div className="px-4 pt-4 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-surface-2 border border-border rounded-xl p-3">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-fg-muted">CP</div>
            <div className="mt-0.5 text-lg font-bold font-mono">{fmt(cp)}</div>
          </div>
          <div className="bg-surface-2 border border-border rounded-xl p-3">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-fg-muted">RP</div>
            <div className="mt-0.5 text-lg font-bold font-mono text-[rgb(255_107_112)]">{fmt(rp)}</div>
          </div>
        </div>

        {msg && <div className="p-3 rounded-xl bg-success/10 border border-success/30 text-success text-sm">{msg}</div>}
        {err && <div className="p-3 rounded-xl bg-danger/10 border border-danger/30 text-danger text-sm">{err}</div>}

        <div className="space-y-3">
          <div className="text-sm font-bold">Select Package</div>
          {PACKAGE_TIERS.map((tier) => {
            const q = qty[tier.items];
            const totalCost = tier.items * q;
            const dailyPct = (tier.dailyRateBps / 100).toFixed(1);
            const capMult = (tier.profitMultiplierX100 / 100).toFixed(1);
            const lifetimeCap = tier.items * q * tier.profitMultiplierX100 / 100;

            const chestT = chestForPackageItems(tier.items);
            const ownsThisTier = owned.some((p) => p.tierItems === tier.items && p.status === 'ACTIVE');

            return (
              <Card key={tier.items} className="!p-0 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <Chest tier={chestT} state={ownsThisTier ? 'open' : 'closed'} size={64} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-baseline gap-2">
                            <div className="text-xl font-extrabold font-mono">{tier.items}</div>
                            <div className="text-xs text-fg-muted font-semibold">USDT pack</div>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[11px]">
                            <Badge tone="accent">{dailyPct}% / day</Badge>
                            <Badge>{capMult}× cap</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] tracking-wider uppercase text-fg-muted">Lifetime Max</div>
                          <div className="text-sm font-bold font-mono">{fmt(lifetimeCap, 0)} USDT</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center bg-surface-2 border border-border rounded-lg">
                      <button
                        onClick={() => setQty((s) => ({ ...s, [tier.items]: Math.max(1, s[tier.items] - 1) }))}
                        className="h-9 w-9 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-40"
                        disabled={q <= 1}
                        type="button"
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={q}
                        onChange={(e) => {
                          const n = Math.max(1, Math.min(50, Number(e.target.value) || 1));
                          setQty((s) => ({ ...s, [tier.items]: n }));
                        }}
                        className="w-12 h-9 bg-transparent text-center font-mono font-bold text-sm focus:outline-none"
                      />
                      <button
                        onClick={() => setQty((s) => ({ ...s, [tier.items]: Math.min(50, s[tier.items] + 1) }))}
                        className="h-9 w-9 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-40"
                        disabled={q >= 50}
                        type="button"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <Button onClick={() => { setBuyTier(tier.items); setErr(null); setMsg(null); }}>
                      Buy · {fmt(totalCost, 0)} USDT
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Owned */}
        <Card padded={false}>
          <div className="p-4 pb-2 flex items-center justify-between">
            <div className="text-sm font-bold">Your Packages</div>
            <Badge>{owned.length}</Badge>
          </div>
          {owned.length === 0 ? (
            <div className="p-6 text-center text-sm text-fg-muted">No packages yet</div>
          ) : (
            <ul className="divide-y divide-border">
              {owned.map((p) => <PackageRow key={p.id} pkg={p} />)}
            </ul>
          )}
        </Card>
      </div>

      <BuyModal
        open={buyTier !== null}
        onClose={() => setBuyTier(null)}
        tier={buyTier}
        quantity={buyTier ? qty[buyTier] : 1}
        wallet={wallet}
        onDone={(note) => { setMsg(note); refresh(); }}
        onErr={setErr}
      />
    </>
  );
}

function PackageRow({ pkg }: { pkg: Pkg }) {
  const tier = chestForPackageItems(pkg.tierItems);
  return (
    <li className="p-3.5 flex items-center gap-3">
      <Chest tier={tier} state={pkg.status === 'ACTIVE' ? 'open' : 'closed'} size={44} className="shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-bold">{pkg.tierItems} USDT pack</div>
          {pkg.status === 'ACTIVE'
            ? <Badge tone="success"><CheckCircle2 size={11} className="-ml-0.5 mr-1" />Active</Badge>
            : pkg.status === 'PURCHASED'
              ? <Badge tone="warning"><Clock size={11} className="-ml-0.5 mr-1" />Pending</Badge>
              : <Badge>Completed</Badge>}
        </div>
        <div className="text-[11px] text-fg-muted mt-0.5">
          Since {new Date(pkg.activatedAt ?? pkg.purchasedAt).toLocaleDateString()} · Paid out {fmt(pkg.paidOut)} USDT
        </div>
      </div>
    </li>
  );
}

function BuyModal({
  open, onClose, tier, quantity, wallet, onDone, onErr,
}: {
  open: boolean; onClose: () => void;
  tier: number | null; quantity: number;
  wallet: Wallet | null;
  onDone: (note: string) => void;
  onErr: (msg: string | null) => void;
}) {
  const [rpPct, setRpPct] = useState<number>(100);
  const [busy, setBusy] = useState(false);

  const totalPrice = (tier ?? 0) * quantity;
  const rpCost = useMemo(() => (totalPrice * rpPct) / 100, [totalPrice, rpPct]);
  const cpCost = useMemo(() => totalPrice - rpCost, [totalPrice, rpCost]);

  const rpBal = Number(wallet?.rpBalance ?? 0);
  const cpBal = Number(wallet?.cpBalance ?? 0);
  const shortRp = rpCost > rpBal;
  const shortCp = cpCost > cpBal;
  const canBuy = !!tier && !shortRp && !shortCp && !busy;

  async function submit() {
    if (!tier) return;
    setBusy(true); onErr(null);
    try {
      const r = await api<{ rpCost: string; cpCost: string }>(
        '/wallet/packages/purchase',
        { method: 'POST', body: JSON.stringify({ items: tier, quantity, rpPercent: rpPct }), signed: true },
      );
      onDone(`Bought ${quantity} × ${tier} USDT pack (−${fmt(r.rpCost)} RP, −${fmt(r.cpCost)} CP)`);
      onClose();
    } catch (e) { onErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title={tier ? `Buy ${tier} USDT pack` : 'Buy pack'}>
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-surface-2 border border-border">
          <div className="flex items-center justify-between">
            <div className="text-xs text-fg-muted">Quantity × Price</div>
            <div className="font-mono font-bold">
              {quantity} × {tier ?? 0} = {fmt(totalPrice, 0)} USDT
            </div>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted mb-2">
            Payment split — RP {rpPct}% / CP {100 - rpPct}%
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {PACKAGE_PAYMENT_SPLITS.map((s) => (
              <button
                key={s.rpPct}
                type="button"
                onClick={() => setRpPct(s.rpPct)}
                className={cn(
                  'py-2 rounded-lg text-[11px] font-bold border transition-colors',
                  rpPct === s.rpPct
                    ? 'bg-accent text-white border-accent'
                    : 'bg-surface-2 text-fg-muted border-border hover:border-accent',
                )}
              >
                {s.rpPct}/{s.cpPct}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <CostBreakdown label="RP to pay" cost={rpCost} balance={rpBal} short={shortRp} tone="rp" />
          <CostBreakdown label="CP to pay" cost={cpCost} balance={cpBal} short={shortCp} tone="cp" />
        </div>

        {(shortRp || shortCp) && (
          <div className="p-3 rounded-xl bg-danger/10 border border-danger/30 text-danger text-xs">
            {shortRp && <div>Insufficient RP ({fmt(rpBal)} available, need {fmt(rpCost)})</div>}
            {shortCp && <div>Insufficient CP ({fmt(cpBal)} available, need {fmt(cpCost)})</div>}
          </div>
        )}

        <Button onClick={submit} fullWidth size="lg" loading={busy} disabled={!canBuy}>
          Confirm Purchase
        </Button>
      </div>
    </Modal>
  );
}

function CostBreakdown({ label, cost, balance, short, tone }: { label: string; cost: number; balance: number; short: boolean; tone: 'rp' | 'cp' }) {
  const color = tone === 'rp' ? 'text-[rgb(255_107_112)]' : 'text-fg';
  return (
    <div className={cn('p-3 rounded-xl border', short ? 'bg-danger/5 border-danger/30' : 'bg-surface-2 border-border')}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted">{label}</div>
      <div className={cn('mt-0.5 font-mono font-bold text-lg', color)}>{fmt(cost)}</div>
      <div className="text-[10px] text-fg-muted mt-0.5">Balance: {fmt(balance)}</div>
    </div>
  );
}
